import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { journeyApi } from '../../api/journeyApi';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import { normalizeMyJourneySummary } from '../my-journeys/myJourneysModel';
import { snowflakePositiveId } from './socialComposeModel';

/**
 * PD-ATT-01：Compose 可選附已公開發布的 Journey publication（不占 media）。
 * FE 組成：listMine + getByJourney；僅 PUBLISHED 可選。
 */
export default function SocialJourneyAttachmentPicker({
  value = null,
  onChange,
  disabled = false,
}) {
  const headingId = useId();
  const generation = useRef(0);
  const [phase, setPhase] = useState('idle');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const selectedId = snowflakePositiveId(value);
  const selected = items.find((item) => item.publicationId === selectedId) || null;

  const load = useCallback(async () => {
    const token = ++generation.current;
    setPhase('loading');
    setError('');
    try {
      const raw = await journeyApi.listMine();
      if (token !== generation.current) return;
      const journeys = Array.isArray(raw) ? raw.map(normalizeMyJourneySummary) : [];
      const enriched = await Promise.all(
        journeys.map(async (journey) => {
          try {
            const pub = await journeyPublicationApi.getByJourney(journey.journeyId);
            const status = pub?.status || '';
            const publicationId = snowflakePositiveId(pub?.publicationId);
            if (status !== 'PUBLISHED' || !publicationId) {
              return null;
            }
            return {
              journeyId: journey.journeyId,
              title: journey.title || '未命名行程',
              publicationId,
            };
          } catch {
            return null;
          }
        }),
      );
      if (token !== generation.current) return;
      setItems(enriched.filter(Boolean));
      setPhase('ready');
    } catch {
      if (token !== generation.current) return;
      setError('無法載入可附行程，請稍後再試。');
      setPhase('error');
    }
  }, []);

  useEffect(() => () => { generation.current += 1; }, []);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return undefined;
  }, [open, load]);

  function clearSelection() {
    onChange?.(null);
  }

  return (
    <section className="social-compose__journey" aria-labelledby={headingId}>
      <div className="social-compose__journey-head">
        <h2 id={headingId}>附上行程（選填）</h2>
        <p>可附一個已公開發布的行程，不占圖片名額。</p>
      </div>

      {selectedId ? (
        <div className="social-compose__journey-selected" data-testid="social-journey-attached">
          <p>
            已附上：
            <strong>{selected?.title || '行程'}</strong>
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={clearSelection}
          >
            移除行程
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="social-compose__journey-toggle"
          disabled={disabled}
          aria-expanded={open}
          data-testid="social-journey-attach-toggle"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? '收合行程列表' : '選擇已發布行程'}
        </button>
      )}

      {open && !selectedId ? (
        <div className="social-compose__journey-panel" role="region" aria-label="可附行程列表">
          {phase === 'loading' ? <p>載入中…</p> : null}
          {phase === 'error' ? (
            <div role="alert">
              <p>{error}</p>
              <button type="button" onClick={load}>重試</button>
            </div>
          ) : null}
          {phase === 'ready' && items.length === 0 ? (
            <p>找不到可附行程？請先發布一個公開行程，再回來附上。</p>
          ) : null}
          {phase === 'ready' && items.length > 0 ? (
            <ul className="social-compose__journey-list">
              {items.map((item) => (
                <li key={item.publicationId}>
                  <button
                    type="button"
                    disabled={disabled}
                    data-testid={`social-journey-option-${item.publicationId}`}
                    onClick={() => {
                      onChange?.(item.publicationId);
                      setOpen(false);
                    }}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
